import * as React from 'react';

import config from '../config';
import { ContextStore } from '../store/contextStore';
import type {
    BreakerAction,
    DeveloperMetrics,
    DeveloperMirror,
    DeveloperOutbound,
    DeveloperOutboundUpdate,
    DeveloperOverview,
    DeveloperRequestDetail,
    OutboxSend,
    OutboxSendDetail,
    Page,
    ReplayAccepted,
    RequestLogItem,
    SandboxBreakerResetResult,
    SandboxSuspensionState,
    VendorCallPage,
    WebhookDelivery,
    WebhookDeliveryDetail,
    WebhookSubscription,
} from './types';
import type { ActivityItem } from './activityTypes';
import type {
    SandboxAdminAddRequest,
    SandboxAdminAdded,
    SandboxResumed,
    SandboxSignin,
    SandboxSigninRequest,
    SandboxSigninTargets,
} from './sandboxTypes';
import type { SandboxSms, SmsTestResult } from './smsTypes';
import type { ApiCredential, ApiCredentialCreate, ApiCredentialCreated, ApiCredentialPage, ApiCredentialRotated, ApiCredentialUpdate } from './apiKeyTypes';
import type {
    Integration,
    IntegrationAccountAdd,
    IntegrationAdd,
    IntegrationCheckRequest,
    IntegrationList,
    IntegrationProbe,
    IntegrationRemoval,
    IntegrationWebhookToken,
    TagCategoryAdd,
    TagCategoryGrid,
    TagCategorySeedResult,
    TagCategoryUpdate,
} from './integrationTypes';

// The Developer section's calls (contract: apiv2/developer.openapi.yaml, keyed by operationId so
// developerContract.test.js can hold every path and method to the contract).
//
// Plain fetch, not the data provider: the provider signs the session out on any 403, while here a
// 403 only means this user lacks `developer.read` / `developer.outbound_manage` -- an answer the page
// shows. It also keeps the franchise wrapper from adding `franchise_to` / `tour_operator` to these
// tenant-scoped calls. A 401 (an expired session) still signs out.
export const ENDPOINTS = {
    developerOverview: { method: 'GET', path: '/v2/edit/developer/overview/' },
    developerSandboxSigninTargets: { method: 'GET', path: '/v2/edit/developer/sandbox/signin/' },
    developerSandboxSignin: { method: 'POST', path: '/v2/edit/developer/sandbox/signin/' },
    developerSandboxResume: { method: 'POST', path: '/v2/edit/developer/sandbox/resume/' },
    developerSandboxAdminAdd: { method: 'POST', path: '/v2/edit/developer/sandbox/admin/' },
    // Production's switch for its sandbox's mirror of production's webhooks (ESB): the card's `mirror` back.
    developerSandboxMirrorUpdate: { method: 'PATCH', path: '/v2/edit/developer/sandbox/mirror/' },
    developerOutboundGet: { method: 'GET', path: '/v2/edit/developer/outbound/' },
    developerOutboundUpdate: { method: 'PATCH', path: '/v2/edit/developer/outbound/' },
    developerMirrorUpdate: { method: 'PATCH', path: '/v2/edit/developer/mirror/' },
    developerSmsGet: { method: 'GET', path: '/v2/edit/developer/sms/' },
    developerSmsSetUp: { method: 'POST', path: '/v2/edit/developer/sms/' },
    developerSmsRemove: { method: 'DELETE', path: '/v2/edit/developer/sms/' },
    developerSmsTest: { method: 'POST', path: '/v2/edit/developer/sms/test/' },
    sandboxSuspensionGet: { method: 'GET', path: '/v2/edit/sandbox/suspension/' },
    sandboxSuspensionSet: { method: 'PUT', path: '/v2/edit/sandbox/suspension/' },
    sandboxBreakerGet: { method: 'GET', path: '/v2/edit/sandbox/breaker/' },
    sandboxBreakerReset: { method: 'POST', path: '/v2/edit/sandbox/breaker/' },
    // API keys (DSEC D1): a 403 only means "not your permission" here, never an expired session.
    apiCredentialList: { method: 'GET', path: '/v2/edit/api_credential/' },
    apiCredentialCreate: { method: 'POST', path: '/v2/edit/api_credential/' },
    apiCredentialUpdate: { method: 'PATCH', path: '/v2/edit/api_credential/{credential_id}/' },
    apiCredentialRevoke: { method: 'POST', path: '/v2/edit/api_credential/{credential_id}/revoke/' },
    apiCredentialRotate: { method: 'POST', path: '/v2/edit/api_credential/{credential_id}/rotate/' },
    developerRequestLog: { method: 'GET', path: '/v2/edit/developer/requests/' },
    developerRequestDetail: { method: 'GET', path: '/v2/edit/developer/requests/{request_id}/' },
    developerRequestReplay: { method: 'POST', path: '/v2/edit/developer/requests/{request_id}/replay/' },
    developerOutbox: { method: 'GET', path: '/v2/edit/developer/outbox/' },
    developerOutboxSend: { method: 'GET', path: '/v2/edit/developer/outbox/{send_id}/' },
    developerWebhooks: { method: 'GET', path: '/v2/edit/developer/webhooks/' },
    developerWebhookTest: { method: 'POST', path: '/v2/edit/developer/webhooks/{subscription_id}/test/' },
    developerWebhookDeliveries: { method: 'GET', path: '/v2/edit/developer/webhook_deliveries/' },
    developerWebhookDelivery: { method: 'GET', path: '/v2/edit/developer/webhook_deliveries/{delivery_id}/' },
    developerWebhookRedeliver: { method: 'POST', path: '/v2/edit/developer/webhook_deliveries/{delivery_id}/redeliver/' },
    developerVendorCalls: { method: 'GET', path: '/v2/edit/developer/vendor_calls/' },
    developerMetrics: { method: 'GET', path: '/v2/edit/developer/metrics/' },
    developerActivity: { method: 'GET', path: '/v2/edit/developer/activity/' },
    // Developer › Integrations (DSEC INT; step-0 contract). Every call takes `franchise_to` explicitly (this
    // client never adds it): pass the selected licensee's id in the query.
    developerIntegrations: { method: 'GET', path: '/v2/edit/developer/integrations/' },
    developerIntegrationAdd: { method: 'POST', path: '/v2/edit/developer/integrations/' },
    developerIntegrationUpdate: { method: 'PATCH', path: '/v2/edit/developer/integrations/{integration_id}/' },
    developerIntegrationRemove: { method: 'DELETE', path: '/v2/edit/developer/integrations/{integration_id}/' },
    developerIntegrationSettings: { method: 'PATCH', path: '/v2/edit/developer/integrations/{integration_id}/settings/' },
    developerIntegrationCredentials: { method: 'POST', path: '/v2/edit/developer/integrations/{integration_id}/credentials/' },
    developerIntegrationCheck: { method: 'POST', path: '/v2/edit/developer/integrations/{integration_id}/check/' },
    developerIntegrationWebhookToken: { method: 'GET', path: '/v2/edit/developer/integrations/{integration_id}/webhook_token/' },
    developerIntegrationInstallWebhooks: { method: 'POST', path: '/v2/edit/developer/integrations/{integration_id}/install_webhooks/' },
    developerIntegrationWebhookSeries: { method: 'GET', path: '/v2/edit/developer/integrations/{integration_id}/webhook_series/' },
    developerIntegrationAccountAdd: { method: 'POST', path: '/v2/edit/developer/integrations/{integration_id}/accounts/' },
    developerIntegrationAccountRemove: { method: 'DELETE', path: '/v2/edit/developer/integrations/{integration_id}/accounts/{slug}/' },
    developerIntegrationAccountSettings: { method: 'PATCH', path: '/v2/edit/developer/integrations/{integration_id}/accounts/{slug}/settings/' },
    developerIntegrationAccountCredentials: { method: 'POST', path: '/v2/edit/developer/integrations/{integration_id}/accounts/{slug}/credentials/' },
    // The tag-category grid (backend plan 2026-10-29 sdtc WP-G): the operator's default tag categories against what
    // the system writes; each write answers the grid.
    developerIntegrationTagCategories: { method: 'GET', path: '/v2/edit/developer/integrations/{integration_id}/tag_categories/' },
    developerIntegrationTagCategoryAdd: { method: 'POST', path: '/v2/edit/developer/integrations/{integration_id}/tag_categories/' },
    developerIntegrationTagCategoryUpdate: { method: 'PATCH', path: '/v2/edit/developer/integrations/{integration_id}/tag_categories/{category_id}/' },
    developerIntegrationTagCategoryRemove: { method: 'DELETE', path: '/v2/edit/developer/integrations/{integration_id}/tag_categories/{category_id}/' },
    developerIntegrationTagCategorySeed: { method: 'POST', path: '/v2/edit/developer/integrations/{integration_id}/tag_categories/seed/' },
} as const;

export type OperationId = keyof typeof ENDPOINTS;

const EDIT_PREFIX = '/v2/edit';

type Query = Record<string, string | number | boolean | null | undefined>;

/** A refused or failed call: `code` is the contract's stable code when the body has one. */
export class DeveloperApiError extends Error {
    status: number;
    code: string | null;
    errors: Array<Record<string, any>>;
    body: any;

    constructor(status: number, body: any) {
        const message = errorMessage(status, body);
        super(message);
        this.name = 'DeveloperApiError';
        this.status = status;
        this.body = body;
        this.code = (body && typeof body.code === 'string' && body.code) || (status === 403 ? 'forbidden' : null);
        this.errors = Array.isArray(body?.errors) ? body.errors : [];
    }
}

function errorMessage(status: number, body: any): string {
    if (body && typeof body.message === 'string' && body.message) {
        return body.message;
    }
    if (body && typeof body.detail === 'string' && body.detail) {
        return body.detail;
    }
    if (status === 0) {
        return 'The server could not be reached. Check your connection and try again.';
    }
    if (status === 403) {
        return 'You do not have permission to do this.';
    }
    if (status === 404) {
        return 'Not found.';
    }
    if (status >= 500) {
        return 'The server could not answer. Try again in a moment.';
    }
    return `The request was refused (${status}).`;
}

export function buildUrl(operation: OperationId, pathParams: Record<string, string | number> = {}, query: Query = {}, apiUrl = config.apiUrl) {
    let path: string = ENDPOINTS[operation].path;
    Object.entries(pathParams).forEach(([name, value]) => {
        path = path.replace(`{${name}}`, encodeURIComponent(String(value)));
    });
    const search = new URLSearchParams();
    Object.entries(query).forEach(([name, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            search.append(name, String(value));
        }
    });
    const qs = search.toString();
    return `${apiUrl}${path.slice(EDIT_PREFIX.length)}${qs ? `?${qs}` : ''}`;
}

interface ClientOptions {
    getToken: () => string | null | undefined;
    onUnauthorized: () => void;
    apiUrl?: string;
}

export function createDeveloperApi({ getToken, onUnauthorized, apiUrl = config.apiUrl }: ClientOptions) {
    const call = async <T,>(
        operation: OperationId,
        { path = {}, query = {}, body, cache = 'no-cache' }: { path?: Record<string, string | number>; query?: Query; body?: any; cache?: RequestCache } = {},
    ): Promise<T> => {
        const { method } = ENDPOINTS[operation];
        const headers: Record<string, string> = { Accept: 'application/json', Authorization: `Token ${getToken()}` };
        if (body !== undefined) {
            headers['Content-Type'] = 'application/json';
        }
        let res: Response;
        try {
            res = await fetch(buildUrl(operation, path, query, apiUrl), {
                method,
                headers,
                cache,
                body: body !== undefined ? JSON.stringify(body) : undefined,
            });
        } catch (_e) {
            throw new DeveloperApiError(0, null);
        }
        let data: any = null;
        const ctype = res.headers.get('Content-Type') || '';
        if (res.status !== 204 && ctype.includes('json')) {
            try {
                data = await res.json();
            } catch (_e) {
                data = null;
            }
        }
        if (res.status === 401) {
            onUnauthorized();
            throw new DeveloperApiError(401, data);
        }
        if (res.status < 200 || res.status >= 300) {
            throw new DeveloperApiError(res.status, data);
        }
        return data as T;
    };

    return {
        overview: () => call<DeveloperOverview>('developerOverview'),
        sandboxSigninTargets: () => call<SandboxSigninTargets>('developerSandboxSigninTargets'),
        sandboxSignin: (body: SandboxSigninRequest = {}) => call<SandboxSignin>('developerSandboxSignin', { body }),
        resumeSandbox: () => call<SandboxResumed>('developerSandboxResume', { body: {} }),
        addSandboxAdmin: (body: SandboxAdminAddRequest) => call<SandboxAdminAdded>('developerSandboxAdminAdd', { body }),
        outbound: () => call<DeveloperOutbound>('developerOutboundGet'),
        updateOutbound: (update: DeveloperOutboundUpdate) => call<DeveloperOutbound>('developerOutboundUpdate', { body: update }),
        setMirror: (enabled: boolean) => call<DeveloperMirror>('developerMirrorUpdate', { body: { enabled } }),
        setSandboxMirror: (enabled: boolean) => call<DeveloperMirror>('developerSandboxMirrorUpdate', { body: { enabled } }),
        sms: () => call<SandboxSms>('developerSmsGet'),
        setUpSms: () => call<SandboxSms>('developerSmsSetUp'),
        removeSms: () => call<SandboxSms>('developerSmsRemove'),
        testSms: (phone: string) => call<SmsTestResult>('developerSmsTest', { body: { phone } }),
        setSuspension: (suspended: boolean, reason?: string) =>
            call<SandboxSuspensionState>('sandboxSuspensionSet', { body: reason ? { suspended, reason } : { suspended } }),
        resetBreaker: (action: BreakerAction) => call<SandboxBreakerResetResult>('sandboxBreakerReset', { body: { action } }),
        apiCredentials: (query: Query) => call<ApiCredentialPage>('apiCredentialList', { query }),
        createApiCredential: (body: ApiCredentialCreate) => call<ApiCredentialCreated>('apiCredentialCreate', { body }),
        updateApiCredential: (id: number, body: ApiCredentialUpdate) => call<ApiCredential>('apiCredentialUpdate', { path: { credential_id: id }, body }),
        revokeApiCredential: (id: number) => call<ApiCredential>('apiCredentialRevoke', { path: { credential_id: id } }),
        rotateApiCredential: (id: number) => call<ApiCredentialRotated>('apiCredentialRotate', { path: { credential_id: id } }),
        requests: (query: Query) => call<Page<RequestLogItem>>('developerRequestLog', { query }),
        request: (requestId: string) => call<DeveloperRequestDetail>('developerRequestDetail', { path: { request_id: requestId } }),
        replay: (requestId: string) => call<ReplayAccepted>('developerRequestReplay', { path: { request_id: requestId } }),
        outbox: (query: Query) => call<Page<OutboxSend>>('developerOutbox', { query }),
        outboxSend: (sendId: number) => call<OutboxSendDetail>('developerOutboxSend', { path: { send_id: sendId } }),
        webhooks: () => call<{ results: WebhookSubscription[] }>('developerWebhooks'),
        testWebhook: (subscriptionId: number) => call<WebhookDelivery>('developerWebhookTest', { path: { subscription_id: subscriptionId }, body: {} }),
        webhookDeliveries: (query: Query) => call<Page<WebhookDelivery>>('developerWebhookDeliveries', { query }),
        webhookDelivery: (deliveryId: string) => call<WebhookDeliveryDetail>('developerWebhookDelivery', { path: { delivery_id: deliveryId } }),
        redeliver: (deliveryId: string) => call<WebhookDelivery>('developerWebhookRedeliver', { path: { delivery_id: deliveryId } }),
        vendorCalls: (query: Query) => call<VendorCallPage>('developerVendorCalls', { query }),
        metrics: (days: number) => call<DeveloperMetrics>('developerMetrics', { query: { days } }),
        activity: (query: Query) => call<Page<ActivityItem>>('developerActivity', { query }),
        integrationsFor: (franchiseTo: number | null) => integrationCalls(call, franchiseTo),
    };
}

type Call = <T>(operation: OperationId, options?: { path?: Record<string, string | number>; query?: Query; body?: any; cache?: RequestCache }) => Promise<T>;

/** Developer › Integrations (DSEC INT), bound to one tenant: `franchiseTo` is a franchise parent's chosen
 *  licensee (null: the user's own tenant), sent explicitly on every call. A `slug` names an account; null is
 *  the Default account, whose settings and credentials are the connection's own endpoints. */
function integrationCalls(call: Call, franchiseTo: number | null) {
    const query = (extra: Query = {}): Query => ({ ...extra, franchise_to: franchiseTo });
    const at = (id: string, slug?: string | null) => (slug ? { integration_id: id, slug } : { integration_id: id });
    return {
        list: () => call<IntegrationList>('developerIntegrations', { query: query() }),
        add: (body: IntegrationAdd) => call<Integration>('developerIntegrationAdd', { query: query(), body }),
        setEnabled: (id: string, enabled: boolean) => call<Integration>('developerIntegrationUpdate', { path: at(id), query: query(), body: { enabled } }),
        /** null (204), or a 200 note: the client must still remove the webhooks at the vendor. */
        remove: (id: string, confirm: string) => call<IntegrationRemoval | null>('developerIntegrationRemove', { path: at(id), query: query({ confirm }) }),
        check: (id: string, body: IntegrationCheckRequest = {}) => call<IntegrationProbe>('developerIntegrationCheck', { path: at(id), query: query(), body }),
        // A secret: never kept in the HTTP cache (the server also answers Cache-Control: no-store).
        webhookToken: (id: string, slug: string | null) => call<IntegrationWebhookToken>(
            'developerIntegrationWebhookToken', { path: at(id), query: query({ account: slug }), cache: 'no-store' },
        ),
        installWebhooks: (id: string, slug: string | null) => call<Integration>('developerIntegrationInstallWebhooks', { path: at(id), query: query(), body: slug ? { account: slug } : {} }),
        saveSettings: (id: string, values: Record<string, any>, slug: string | null = null) => call<Integration>(
            slug ? 'developerIntegrationAccountSettings' : 'developerIntegrationSettings', { path: at(id, slug), query: query(), body: { values } },
        ),
        replaceCredentials: (id: string, credentials: Record<string, any>, slug: string | null = null) => call<Integration>(
            slug ? 'developerIntegrationAccountCredentials' : 'developerIntegrationCredentials', { path: at(id, slug), query: query(), body: { credentials } },
        ),
        addAccount: (id: string, body: IntegrationAccountAdd) => call<Integration>('developerIntegrationAccountAdd', { path: at(id), query: query(), body }),
        removeAccount: (id: string, slug: string, confirm: string) => call<IntegrationRemoval | null>('developerIntegrationAccountRemove', { path: at(id, slug), query: query({ confirm }) }),
        // The tag-category grid: the operator-wide defaults seen from one booking system. Writes need
        // `tour_operator.write` (the grid's `editable`); each answers the grid as it is after the change.
        tagCategories: (id: string) => call<TagCategoryGrid>('developerIntegrationTagCategories', { path: at(id), query: query() }),
        addTagCategory: (id: string, body: TagCategoryAdd) => call<TagCategoryGrid>('developerIntegrationTagCategoryAdd', { path: at(id), query: query(), body }),
        updateTagCategory: (id: string, categoryId: number, body: TagCategoryUpdate) => call<TagCategoryGrid>(
            'developerIntegrationTagCategoryUpdate', { path: { integration_id: id, category_id: categoryId }, query: query(), body },
        ),
        removeTagCategory: (id: string, categoryId: number) => call<TagCategoryGrid>(
            'developerIntegrationTagCategoryRemove', { path: { integration_id: id, category_id: categoryId }, query: query() },
        ),
        /** "Add all recommended": `dryRun` is the confirm dialog's preview (nothing written). */
        seedTagCategories: (id: string, dryRun: boolean) => call<TagCategorySeedResult>(
            'developerIntegrationTagCategorySeed', { path: at(id), query: query(dryRun ? { dry_run: 1 } : {}), body: {} },
        ),
    };
}

export type IntegrationsApi = ReturnType<typeof integrationCalls>;

export type DeveloperApi = ReturnType<typeof createDeveloperApi>;

/** The Developer API for the signed-in session. Identity-stable: the token is read per call (it
 *  rotates), and a 401 signs out and comes back to the page the user was on. */
export function useDeveloperApi(): DeveloperApi {
    const { token, logout } = React.useContext(ContextStore) as any;
    const tokenRef = React.useRef(token);
    tokenRef.current = token;
    const logoutRef = React.useRef(logout);
    logoutRef.current = logout;
    return React.useMemo(
        () => createDeveloperApi({
            getToken: () => tokenRef.current,
            onUnauthorized: () => logoutRef.current?.(window.location.pathname),
        }),
        [],
    );
}
